import { useEffect } from 'react'
import { Trash2 } from 'lucide-react'
import type { Control, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from 'react-hook-form'
import { useTranslations } from '@/hooks/useTranslations'

import { ManagedPicklistCombobox } from '@/components/pages/Admin/BillManagement/Form/ManagedPicklistCombobox'
import {
  emptyExtraCharge,
  extraChargeRowAmount,
  isExtraChargeFilled,
  type BillExtraChargeFormValue,
} from '@/components/pages/Admin/BillManagement/Form/billLineItemsShared'
import {
  excelCellInputClass,
  type BillFormGridVariant,
} from '@/components/pages/Admin/BillManagement/Form/billFormViewShared'
import { useBillLineGridKeyboard } from '@/components/pages/Admin/BillManagement/Form/useBillLineGridKeyboard'
import { Button, FormControl, FormField, FormItem, FormMessage, Input } from '@/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

type BillExtraChargesGridProps = {
  control: Control<any>
  fields: FieldArrayWithId<any, 'bill_information.extra_charges', 'id'>[]
  append: UseFieldArrayAppend<any, 'bill_information.extra_charges'>
  remove: UseFieldArrayRemove
  extraCharges: BillExtraChargeFormValue[] | undefined
  disabled?: boolean
  variant?: BillFormGridVariant
}

const COL_COUNT = 3

export function BillExtraChargesGrid({
  control,
  fields,
  append,
  remove,
  extraCharges,
  disabled,
  variant = 'default',
}: BillExtraChargesGridProps) {
  const t = useTranslations('admin.bill_management.form')
  const isExcel = variant === 'excel'
  const { gridCellProps } = useBillLineGridKeyboard({
    rowCount: fields.length,
    editableColCount: COL_COUNT,
    onAppendRow: () => append(emptyExtraCharge()),
  })

  useEffect(() => {
    if (!extraCharges?.length) return
    const last = extraCharges[extraCharges.length - 1]
    if (isExtraChargeFilled(last)) {
      append(emptyExtraCharge())
    }
  }, [append, extraCharges])

  const inputClass = cn(
    isExcel
      ? cn(excelCellInputClass, 'tabular-nums')
      : 'h-9 border-border/80 bg-background px-2 text-sm tabular-nums',
  )
  const cellClass = cn(isExcel ? 'border border-border/60 p-0' : 'px-2 py-1.5')
  const headClass = cn(
    isExcel
      ? 'h-8 border border-border/60 bg-muted/50 px-1.5 text-xs font-semibold'
      : 'h-10 px-2',
  )

  return (
    <div
      className={cn(
        'overflow-x-auto',
        !isExcel && 'mt-2 rounded-lg border border-border/70',
        isExcel && 'mt-0',
      )}
    >
      <Table className={cn('min-w-[40rem] text-sm', isExcel && 'border-collapse')}>
        <TableHeader>
          <TableRow className={cn('hover:bg-muted/40', isExcel ? 'bg-muted/50' : 'bg-muted/40')}>
            <TableHead className={cn(headClass, 'w-10 text-center')}>#</TableHead>
            <TableHead className={cn(headClass, 'min-w-[12rem]')}>ประเภทค่าใช้จ่าย</TableHead>
            <TableHead className={cn(headClass, 'min-w-[6rem]')}>
              {t('bill_information.wholesale_extra_charge_qty')}
            </TableHead>
            <TableHead className={cn(headClass, 'min-w-[7rem]')}>
              {t('bill_information.wholesale_extra_charge_unit_price')}
            </TableHead>
            <TableHead className={cn(headClass, 'min-w-[6rem] text-right')}>
              {t('bill_information.wholesale_extra_charge_total')}
            </TableHead>
            <TableHead className={cn(headClass, 'w-10')} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((fieldRow, index) => {
            const rowTotal = extraChargeRowAmount(extraCharges?.[index] ?? {})
            return (
              <TableRow
                key={fieldRow.id}
                className={cn('align-top', !isExcel && 'hover:bg-muted/20')}
              >
                <TableCell
                  className={cn(
                    cellClass,
                    'text-center text-xs text-muted-foreground',
                    isExcel && 'bg-muted/30 px-1.5 py-1',
                  )}
                >
                  {index + 1}
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`bill_information.extra_charges.${index}.expense_type_name`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <ManagedPicklistCombobox
                            kind="expense_type"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder="เลือกหรือเพิ่มประเภทค่าใช้จ่าย"
                            disabled={disabled}
                            className={isExcel ? excelCellInputClass : undefined}
                            gridRow={index}
                            gridCol={0}
                            onGridKeyDown={gridCellProps(index, 0).onKeyDown}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`bill_information.extra_charges.${index}.quantity`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            className={inputClass}
                            disabled={disabled}
                            value={field.value === 0 ? '' : field.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.onChange(v === '' ? 0 : parseInt(v, 10))
                            }}
                            placeholder={t('bill_information.wholesale_extra_charge_qty')}
                            {...gridCellProps(index, 1)}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell className={cellClass}>
                  <FormField
                    control={control}
                    name={`bill_information.extra_charges.${index}.unit_price_baht`}
                    render={({ field }) => (
                      <FormItem className="space-y-0">
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            className={inputClass}
                            disabled={disabled}
                            value={field.value === 0 ? '' : field.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.onChange(v === '' ? 0 : parseFloat(v))
                            }}
                            placeholder={t('bill_information.wholesale_extra_charge_unit_price')}
                            {...gridCellProps(index, 2)}
                          />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell
                  className={cn(
                    cellClass,
                    'text-right text-sm font-medium tabular-nums',
                    isExcel && 'px-1.5 py-1',
                  )}
                >
                  {rowTotal > 0 ? rowTotal.toFixed(2) : ''}
                </TableCell>
                <TableCell className={cn(cellClass, isExcel && 'px-0.5 py-0.5')}>
                  {fields.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => remove(index)}
                      aria-label="ลบค่าใช้จ่าย"
                      tabIndex={-1}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
